/**
 * Authentication UI components for login/logout functionality
 * Integrates with the AuthProvider to manage user authentication state
 */

import React from 'react';
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  PersonRegular,
  SignOutRegular,
  ShieldPersonRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../auth/AuthProvider';
import { UserProfile } from './UserProfile';

const useStyles = makeStyles({
  authContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  userName: {
    fontWeight: tokens.fontWeightSemibold,
    color: 'var(--color-text-primary)',
  },
  loginButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  errorMessage: {
    marginBottom: tokens.spacingVerticalM,
  },
});

/**
 * Login button for unauthenticated users
 */
export const LoginButton: React.FC = () => {
  const classes = useStyles();
  const { login, loginInProgress, error, errorType } = useAuth();

  const handleLogin = () => {
    login().catch(console.error);
  };

  return (
    <div className={classes.authContainer}>
      {error && (
        <MessageBar 
          intent={errorType === 'access_denied' ? 'warning' : 'error'} 
          className={classes.errorMessage}
        >
          <MessageBarBody>
            {errorType === 'access_denied' ? (
              <div>
                <strong>Access Denied</strong><br />
                {error}
              </div>
            ) : (
              error
            )}
          </MessageBarBody>
        </MessageBar>
      )}
      <Button
        appearance="primary"
        icon={loginInProgress ? <Spinner size="tiny" /> : <PersonRegular />}
        onClick={handleLogin}
        disabled={loginInProgress}
        className={classes.loginButton}
      >
        {loginInProgress ? 'Signing in...' : 'Sign in with Microsoft'}
      </Button>
    </div>
  );
};

/**
 * User profile menu for authenticated users
 */
export const UserProfileMenu: React.FC = () => {
  const classes = useStyles();
  const { user, logout, loginInProgress } = useAuth();

  const handleLogout = () => {
    logout().catch(console.error);
  };

  if (!user) {
    return null;
  }

  return (
    <div className={classes.authContainer}>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <MenuButton
            appearance="subtle"
            disabled={loginInProgress}
          >
            <UserProfile size="small" showFullProfile={false} />
          </MenuButton>
        </MenuTrigger>

        <MenuPopover>
          <MenuList>
            <MenuItem icon={<PersonRegular />} disabled>
              {/* Enhanced profile information in menu */}
              <UserProfile size="medium" showFullProfile={true} />
            </MenuItem>
            
            <MenuItem 
              icon={<ShieldPersonRegular />}
              disabled
            >
              Account Type: {user.environment || 'Standard'}
            </MenuItem>
            
            <MenuItem 
              icon={<SignOutRegular />}
              onClick={handleLogout}
              disabled={loginInProgress}
            >
              Sign out
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};

/**
 * Main authentication component that shows login or user profile based on auth state
 */
export const AuthenticationButton: React.FC = () => {
  const { isAuthenticated, loginInProgress } = useAuth();

  if (loginInProgress) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
        <Spinner size="tiny" />
        <span>Authenticating...</span>
      </div>
    );
  }

  return isAuthenticated ? <UserProfileMenu /> : <LoginButton />;
};

export default AuthenticationButton;
